import { useEffect, useRef } from 'react';
import { Video } from 'lucide-react';
import type { AisleWalkthrough } from '../types';
import { resolveWalkthroughVideoUrl } from '../mock-data/walkthrough-videos';
import {
  walkthroughBrowserRotationDegrees,
  walkthroughVideoObjectClass,
  walkthroughVideoRotationStyle,
} from '../lib/walkthroughVideoRotation';

interface WalkthroughCardPreviewProps {
  walk: AisleWalkthrough;
}

/**
 * Prefer a stored JPEG poster. If none exists yet, show a paused video frame
 * so cards are never blank while server thumbnail generation catches up.
 */
export function WalkthroughCardPreview({ walk }: WalkthroughCardPreviewProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const videoUrl = resolveWalkthroughVideoUrl(walk.id, walk.videoUrl);
  const rotation = walkthroughBrowserRotationDegrees(videoUrl, walk.videoRotationDegrees);
  const objectClass = walkthroughVideoObjectClass(rotation);
  const rotationStyle = walkthroughVideoRotationStyle(rotation);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || walk.hasRealThumbnail) return;

    const showFrame = () => {
      if (!Number.isFinite(video.duration) || video.duration <= 0) return;
      const target = Math.min(1, Math.max(0.05, video.duration * 0.08));
      if (Math.abs(video.currentTime - target) > 0.04) {
        video.currentTime = target;
      }
    };

    video.addEventListener('loadeddata', showFrame);
    video.addEventListener('loadedmetadata', showFrame);
    if (video.readyState >= 2) {
      showFrame();
    }
    return () => {
      video.removeEventListener('loadeddata', showFrame);
      video.removeEventListener('loadedmetadata', showFrame);
    };
  }, [videoUrl, walk.hasRealThumbnail, walk.id]);

  if (walk.hasRealThumbnail) {
    return (
      <img
        src={walk.thumbnailUrl}
        alt={walk.title}
        className="w-full h-32 object-cover rounded-t-lg bg-gray-900"
      />
    );
  }

  if (videoUrl) {
    return (
      <div className="relative w-full h-32 overflow-hidden rounded-t-lg bg-gray-900">
        <video
          ref={videoRef}
          src={videoUrl}
          muted
          playsInline
          preload="metadata"
          className={`absolute inset-0 w-full h-full ${objectClass}`}
          style={rotationStyle}
        />
        <div className="absolute inset-0 flex items-center justify-center bg-black/25 pointer-events-none">
          <Video className="h-10 w-10 text-white opacity-80" />
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full h-32 rounded-t-lg bg-gray-900">
      <img
        src={walk.thumbnailUrl}
        alt={walk.title}
        className="w-full h-32 object-cover rounded-t-lg opacity-80"
      />
      <div className="absolute inset-0 flex items-center justify-center bg-black/30">
        <Video className="h-10 w-10 text-white opacity-80" />
      </div>
    </div>
  );
}
